import type { User, D1Database } from '../types/index.d.ts';

type AdminRole = 'super_admin' | 'admin' | 'finance_admin' | 'town_admin' | 'member_admin' | 'developer' | 'member' | 'member_portal_admin';

export function isUser(user: User | null | undefined): user is User {
  return Boolean(user);
}

// Role-based access control functions (admins + members)
export function isAdmin(user: User | null | undefined): user is User {
  if (!isUser(user)) return false;
  const adminRoles: AdminRole[] = ['admin', 'finance_admin', 'town_admin', 'developer'];
  return adminRoles.includes(user.role as AdminRole) || user.originalUserRole === 'developer';
}

export function isMemberAdmin(user: User | null | undefined): user is User {
  if (!isUser(user)) return false;
  const memberAdminRoles: AdminRole[] = ['admin', 'member_admin', 'town_admin', 'developer'];
  return memberAdminRoles.includes(user.role as AdminRole) || user.originalUserRole === 'developer';
}

export function isSuperAdmin(user: User | null | undefined): user is User {
  if (!isUser(user)) return false;
  const superAdminRoles: AdminRole[] = ['super_admin', 'admin', 'developer'];
  return superAdminRoles.includes(user.role as AdminRole) ||
         user.originalUserRole === 'developer';
}

export function isFinanceAdmin(user: User | null | undefined): user is User {
  if (!isUser(user)) return false;
  const financeAdminRoles: AdminRole[] = ['admin', 'finance_admin', 'developer'];
  return financeAdminRoles.includes(user.role as AdminRole) || user.originalUserRole === 'developer';
}

export function isMember(user: User | null | undefined): user is User {
  if (!isUser(user)) return false;
  return (user.role as AdminRole) === 'member' || (user.role as AdminRole) === 'member_portal_admin';
}

// Helper functions for brand settings
export async function getBrandSettings(db: D1Database) {
  try {
    // Ensure table exists first
    await db.prepare(`
      CREATE TABLE IF NOT EXISTS brand_settings (
        id TEXT PRIMARY KEY,
        data TEXT NOT NULL,
        lastUpdated TEXT DEFAULT CURRENT_TIMESTAMP
      )
    `).run();
    
    const result = await db.prepare("SELECT data FROM brand_settings WHERE id = 'brand_config'")
      .first<{ data: string }>();
    return result ? JSON.parse(result.data) : {};
  } catch (_error) {
    return {};
  }
}

// Input sanitization for logs
export const sanitizeForLog = (input: string): string => {
  if (typeof input !== 'string') return '';
  return input
    .replace(/[\r\n]/g, ' ')
    .replace(/[^\x20-\x7E]/g, '')
    .substring(0, 200)
    .trim();
};
